import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import Screen from '../../components/common/Screen';
import Card from '../../components/elements/Card';
import Title from '../../components/common/Title';
import Icon from '../../components/common/Icon';
import NotFound from '../../components/common/NotFound';
import ReCaptchaWidget from '../../components/forms/ReCaptchaWidget';
import BravosSurveyFlow from '../event/BravosSurveyFlow';
import WorkingOnFeedback from '../event/WorkingOnFeedback';
import { apiRequest } from '../../utils';
import './PublicReadiness.css';

/* Public, unauthenticated Bravos scan (the lead-magnet teaser).
   tornea.app/readiness/<slug> — a prospect answers the paginated scan (with admin-managed
   quote/visual cards interleaved), sees tailored results IMMEDIATELY (read-only preview), and can
   then opt in to have them emailed — which is where we capture their contact details as a CRM lead.
   Backed by GET + POST /preview + POST /submit (routes/public_readiness.py). The email/lead-capture
   submit is gated by reCAPTCHA.

   ★ The same flow serves every public scan that returns the {blocks, focus} feedback shape: the
   readiness scan (the defaults below) and the Engagement Stack Scan (see PublicStackScan.js).
   Only the API prefix and the scan-specific wording differ, so they are props rather than a
   second copy of this file — the generic field labels and the whole visual layer stay shared. */

// Scan-specific wording, as i18n keys. A caller overrides only what its scan words differently;
// anything it leaves out (the form labels, the generic errors) falls back to the readiness copy.
const DEFAULT_COPY = {
  title: 'readinessTitle',
  intro: 'readinessIntro',
  notFound: 'readinessNotFound',
  contactTitle: 'readinessContactTitle',
  contactIntro: 'readinessContactIntro',
  consent: 'readinessConsent',
  mailMe: 'readinessMailMe',
  emailed: 'readinessEmailedNote',
  submitError: 'readinessSubmitError',
  retry: 'readinessRetry',
  resultsTitle: 'readinessResultsTitle',
  ctaButton: 'readinessCtaButton',
  joinNow: 'readinessJoinNow',
  errorRequired: 'readinessErrorRequired',
  errorEmail: 'readinessErrorEmail',
  errorPhone: 'readinessErrorPhone',
  errorConsent: 'readinessErrorConsent',
  answerAll: 'readinessAnswerAll',
};

// Server ERR_* codes -> i18n keys (server stays authoritative; client mirrors for instant UX).
const serverErrorKeys = (c) => ({
  ERR_REQUIRED: c.errorRequired,
  ERR_INVALID_EMAIL: c.errorEmail,
  ERR_INVALID_PHONE: c.errorPhone,
  ERR_CONSENT_REQUIRED: c.errorConsent,
  ERR_NO_ANSWERS: c.answerAll,
});

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MIN_WORKING_MS = 7000;   // let the AI-style "working on your feedback" animation play out

const PublicReadiness = ({ apiBase = 'public-readiness', copy, idPrefix = 'rd' }) => {
  const { t } = useTranslation();
  const { slug } = useParams();
  const navigate = useNavigate();
  const c = useMemo(() => ({ ...DEFAULT_COPY, ...(copy || {}) }), [copy]);

  const [config, setConfig] = useState(null);          // GET payload
  const [status, setStatus] = useState('loading');      // loading | ok | notfound
  const [stage, setStage] = useState('questions');      // questions | working | results | contact

  const [answers, setAnswers] = useState({});
  const [contact, setContact] = useState({
    first_name: '', last_name: '', email: '', phone: '', company: '', job_title: '', hp: '',
  });
  const [consent, setConsent] = useState(false);
  const [captchaToken, setCaptchaToken] = useState('');
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);           // { feedback, cta_url }
  const [emailed, setEmailed] = useState(false);

  useEffect(() => {
    if (!slug) { setStatus('notfound'); return undefined; }
    let cancelled = false;
    setStatus('loading');
    apiRequest('GET', `/${apiBase}/${slug}`)
      .then((res) => { if (!cancelled) { setConfig(res); setStatus('ok'); } })
      .catch(() => { if (!cancelled) setStatus('notfound'); });
    return () => { cancelled = true; };
  }, [slug, apiBase]);

  const questions = useMemo(() => (config && config.questions) || [], [config]);
  const interstitials = useMemo(() => (config && config.interstitials) || [], [config]);
  const recaptchaSiteKey = (config && config.recaptcha_site_key) || null;

  const setAnswer = (id, v) => setAnswers((a) => ({ ...a, [id]: v }));
  const setField = (k, v) => setContact((prev) => ({ ...prev, [k]: v }));

  const revealAfter = (started, fn) =>
    setTimeout(fn, Math.max(0, MIN_WORKING_MS - (Date.now() - started)));

  // After the last question: compute + show the results (read-only, no capture).
  const computeResults = async () => {
    setFormError(null);
    setStage('working');
    const started = Date.now();
    try {
      const resp = await fetch(`/api/${apiBase}/${slug}/preview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ submit_token: config.submit_token, answers }),
      });
      if (resp.ok) {
        const data = await resp.json();
        revealAfter(started, () => { setResult(data); setStage('results'); window.scrollTo(0, 0); });
        return;
      }
      revealAfter(started, () => {
        setResult(null); setFormError(t(c.submitError));
        setStage('results'); window.scrollTo(0, 0);
      });
    } catch (err) {
      revealAfter(started, () => {
        setResult(null); setFormError(t(c.submitError));
        setStage('results'); window.scrollTo(0, 0);
      });
    }
  };

  const validateContact = () => {
    const e = {};
    if (!contact.first_name.trim()) e.first_name = t(c.errorRequired);
    if (!contact.last_name.trim()) e.last_name = t(c.errorRequired);
    if (!contact.email.trim()) e.email = t(c.errorRequired);
    else if (!EMAIL_RE.test(contact.email.trim())) e.email = t(c.errorEmail);
    // phone + company are optional.
    if (!consent) e.consent = t(c.errorConsent);
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  // Go back to the contact form and surface an error (used for any non-OK mail submit).
  const failToContact = (msg) => {
    setStage('contact');
    setCaptchaToken('');   // reCAPTCHA tokens are single-use — force a fresh challenge on retry
    setSubmitting(false);
    if (msg) setFormError(msg);
    window.scrollTo(0, 0);
  };

  // "Mail me my results": capture the lead + send the follow-up email.
  const mailResults = async () => {
    if (!validateContact()) return;
    if (recaptchaSiteKey && !captchaToken) { setFormError(t('completeCaptcha')); return; }
    setSubmitting(true);
    setFormError(null);
    setStage('working');
    const started = Date.now();

    try {
      const resp = await fetch(`/api/${apiBase}/${slug}/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          submit_token: config.submit_token,
          answers,
          first_name: contact.first_name,
          last_name: contact.last_name,
          email: contact.email,
          phone: contact.phone,
          company: contact.company,
          job_title: contact.job_title,
          consent,
          recaptcha_token: captchaToken,
          hp: contact.hp || '',
        }),
      });

      if (resp.ok) {
        const data = await resp.json();
        revealAfter(started, () => {
          setResult((r) => (data && data.feedback ? data : r));
          setEmailed(true);
          setSubmitting(false);
          setStage('results');
          window.scrollTo(0, 0);
        });
        return;
      }

      const data = await resp.json().catch(() => ({}));
      if (resp.status === 400 && data.error === 'ERR_CAPTCHA') { failToContact(t('captchaFailed')); return; }
      if (resp.status === 400 && data.errors) {
        const mapped = {};
        Object.entries(data.errors).forEach(([k, code]) => {
          mapped[k] = t(serverErrorKeys(c)[code] || c.errorRequired);
        });
        setErrors(mapped);
        failToContact(mapped.answers || null);
        return;
      }
      failToContact(t(c.submitError));
    } catch (err) {
      failToContact(t(c.submitError));
    }
  };

  const brandName = (config && config.bu_name) || 'Bravos';
  const accent = (config && config.primary_color) || null;
  const accentStyle = accent ? { '--readiness-accent': accent } : undefined;

  // ----- loading / not found -----
  if (status === 'loading') {
    return <Screen><div className="top"><NotFound translationKey="loading" loading={true} /></div></Screen>;
  }
  if (status === 'notfound' || !config) {
    return (
      <Screen><div className="top">
        <Card>
          <Title><Icon name="info" className="me-2" theme="primary" />{t(c.title)}</Title>
          <p className="text-muted">{t(c.notFound)}</p>
        </Card>
      </div></Screen>
    );
  }

  const field = (key, labelKey, type = 'text', optional = false) => (
    <div className="mb-3 text-start">
      <label className="form-label fw-bold" htmlFor={`${idPrefix}_${key}`}>
        {t(labelKey)}{!optional && <span className="text-danger"> *</span>}
      </label>
      <input
        id={`${idPrefix}_${key}`}
        type={type}
        className={`form-control${errors[key] ? ' is-invalid' : ''}`}
        value={contact[key]}
        onChange={(e) => setField(key, e.target.value)}
      />
      {errors[key] && <div className="invalid-feedback d-block">{errors[key]}</div>}
    </div>
  );

  return (
    <Screen><div className="top">
      <Card>
        <div className="readiness" style={accentStyle}>
          {config.logo_url && (
            <img src={config.logo_url} alt={brandName} className="readiness__logo" />
          )}

          {/* ---------- stage: questions (paginated + interstitials) ---------- */}
          {stage === 'questions' && (
            <BravosSurveyFlow
              questions={questions}
              interstitials={interstitials}
              answers={answers}
              setAnswer={setAnswer}
              onComplete={computeResults}
              header={(
                <>
                  <Title>{t(c.title)}</Title>
                  <p className="text-muted text-start mx-2">{t(c.intro)}</p>
                </>
              )}
            />
          )}

          {/* ---------- stage: working animation ---------- */}
          {stage === 'working' && <WorkingOnFeedback />}

          {/* ---------- stage: results (shown first; mail-me is opt-in) ---------- */}
          {stage === 'results' && (
            result && result.feedback ? (
              <ReadinessResults
                t={t} c={c} feedback={result.feedback} ctaUrl={result.cta_url} navigate={navigate}
                emailSlot={emailed ? (
                  <p className="text-success readiness-emailed">
                    <Icon name="check" className="me-1" />{t(c.emailed)}
                  </p>
                ) : (
                  <button type="button" className="bravos-submit"
                          onClick={() => { setStage('contact'); setFormError(null); window.scrollTo(0, 0); }}>
                    <Icon name="envelope" /> {t(c.mailMe)}
                  </button>
                )}
              />
            ) : (
              <div className="text-center py-3">
                <p className="text-danger">{formError || t(c.submitError)}</p>
                <button type="button" className="bravos-submit" onClick={computeResults}>
                  <Icon name="refresh" /> {t(c.retry)}
                </button>
              </div>
            )
          )}

          {/* ---------- stage: contact gate ("almost there") ---------- */}
          {stage === 'contact' && (
            <>
              <Title>{t(c.contactTitle)}</Title>
              <p className="text-muted text-start mx-2">{t(c.contactIntro)}</p>

              {field('first_name', 'readinessFirstName')}
              {field('last_name', 'readinessLastName')}
              {field('email', 'readinessEmail', 'email')}
              {field('phone', 'readinessPhone', 'tel', true)}
              {field('company', 'readinessCompany', 'text', true)}
              {field('job_title', 'readinessJobTitle', 'text', true)}

              {/* Honeypot — hidden from humans. */}
              <div aria-hidden="true"
                   style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px', overflow: 'hidden' }}>
                <label htmlFor={`${idPrefix}_hp`}>{t('formLeaveBlank')}</label>
                <input type="text" id={`${idPrefix}_hp`} tabIndex={-1} autoComplete="off"
                       value={contact.hp} onChange={(e) => setField('hp', e.target.value)} />
              </div>

              <div className="mb-3 text-start">
                <div className="form-check">
                  <input className={`form-check-input${errors.consent ? ' is-invalid' : ''}`}
                         type="checkbox" id={`${idPrefix}_consent`}
                         checked={consent} onChange={(e) => setConsent(e.target.checked)} />
                  <label className="form-check-label" htmlFor={`${idPrefix}_consent`}>
                    {t(c.consent)}
                  </label>
                  {errors.consent && <div className="invalid-feedback d-block">{errors.consent}</div>}
                </div>
              </div>

              {recaptchaSiteKey && (
                <div className="mb-3 d-flex justify-content-center">
                  <ReCaptchaWidget
                    siteKey={recaptchaSiteKey}
                    onVerify={(token) => setCaptchaToken(token)}
                    onExpire={() => setCaptchaToken('')}
                    onError={() => setCaptchaToken('')}
                  />
                </div>
              )}

              {formError && <div className="alert alert-danger" role="alert">{formError}</div>}

              <button type="button" className="bravos-submit" onClick={mailResults} disabled={submitting}>
                {submitting ? <span className="auth__spinner" /> : <Icon name="envelope" />}
                {t(c.mailMe)}
              </button>
              <button type="button" className="bravos-submit bravos-submit--ghost"
                      onClick={() => { setStage('results'); setFormError(null); }}>
                <Icon name="arrow-left" /> {t('back')}
              </button>
            </>
          )}
        </div>
      </Card>
    </div></Screen>
  );
};

// The results view: a hero archetype block, the per-question insights, the mail-me opt-in, and
// finally the join-Bravos CTA. The blocks fade in one after another (staggered) so the snapshot
// builds up dynamically rather than appearing all at once. `emailSlot` (the "email me my results"
// button or the emailed confirmation) is rendered ABOVE the why-join block.
const ReadinessResults = ({ t, c, feedback, ctaUrl, navigate, emailSlot }) => {
  const blocks = feedback.blocks || [];
  const hero = blocks[0];
  const insights = blocks.slice(1);
  const focus = feedback.focus;

  // Each rendered block gets an increasing animation-delay, so they reveal in sequence.
  let step = 0;
  const reveal = () => ({ animationDelay: `${(step++) * 0.12 + 0.08}s` });

  return (
    <div className="readiness-results">
      <div className="readiness-recorded">
        <Icon name="check" /> {t(c.resultsTitle)}
      </div>

      {hero && (
        <div className="readiness-hero readiness-reveal" style={reveal()}>
          {hero.bucket && <h2 className="readiness-hero__headline">{hero.bucket}</h2>}
          {hero.feedback && <p className="readiness-hero__text">{hero.feedback}</p>}
        </div>
      )}

      {insights.map((b, i) => (
        <div key={i} className="readiness-insight readiness-reveal" style={reveal()}>
          {b.bucket && <p className="readiness-insight__label">{b.bucket}</p>}
          {b.feedback && <p className="readiness-insight__text">{b.feedback}</p>}
        </div>
      ))}

      {/* Mail-me opt-in sits above the why-join block. */}
      {emailSlot && (
        <div className="readiness-mailme readiness-reveal" style={reveal()}>{emailSlot}</div>
      )}

      {focus && (focus.bravos_value || focus.bucket) && (
        <div className="readiness-cta readiness-reveal" style={reveal()}>
          {focus.bucket && <p className="readiness-cta__title">{focus.bucket}</p>}
          {focus.bravos_value && <p className="readiness-cta__text">{focus.bravos_value}</p>}
          {ctaUrl && (
            <div className="readiness-cta__actions">
              <button type="button" className="bravos-submit bravos-submit--ghost"
                      onClick={() => navigate(ctaUrl)}>
                <Icon name="arrow-right" /> {t(c.ctaButton)}
              </button>
              <button type="button" className="bravos-submit" onClick={() => navigate(ctaUrl)}>
                {t(c.joinNow)} <Icon name="arrow-right" />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PublicReadiness;
